import { useState, useEffect, useContext } from "react";
import { SiteContext } from "../contexts/SiteContext";
import { AuthProvider, useAuth } from "../admin/contexts/AuthContext";
import fetchSite from "../utils/fetchSite";
import { useParams } from "react-router-dom";
import { Paper, Typography, makeStyles, Fade } from "@material-ui/core";
import Loading from "../components/Loading";
import Header from "../components/Header";
import AppBar from "../components/AppBar";
import Breadcrumbs from "../components/Breadcrumbs";
import NotFound from "../components/NotFound";
import AdminBar from "../components/AdminBar";
import NotAllowed from "../components/NotAllowed";
import Banner from "../components/Banner";

function Page() {
  const { isAuth } = useAuth();
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState();
  const { selectedSite, setSelectedSite } = useContext(SiteContext);
  const { siteSlug, pageSlug } = useParams();

  const useStyles = makeStyles((theme) => ({
    notAllowed: {
      margin: "1rem",
    },
    root: {
      flex: 1,
      paddingBottom: "4rem",
    },
    main: {
      margin: "0 auto",
      padding: "1rem",
      paddingTop: 0,
      maxWidth: process.env.REACT_APP_MAX_WIDTH,
    },
    paper: {
      padding: "1rem",
      type: "dark",
      maxWidth: '1024px',
      margin: '0 auto',
      '& .raw-html-embed': {
        display: 'flex'
      },
      '& iframe': {
        maxWidth: '100%',
        margin: '0 auto',
      },
      '& img': {
        maxWidth: '100%'
      }
    },
    featuredImage: {
      width: "100%",
    },
    button: {
      fontWeight: "bold",
      fontSize: ".7rem",
    },
    linkToMainPage: {
      color:
        selectedSite && selectedSite.secondaryColor
          ? selectedSite.secondaryColor
          : theme.palette.secondary.main,
    },
  }));

  const classes = useStyles();

  // This effect sets the selectedSite based on the :siteSlug (first url parameter)
  useEffect(() => {
    fetchSite(siteSlug, setSelectedSite);
  }, [siteSlug, setSelectedSite]);

  // This effect sets the page and uses the selectedSite._id to make sure it's on the right site
  useEffect(() => {
    async function fetchPage() {
      let res = await fetch(
        `${process.env.REACT_APP_API_URL}/pages?site=${selectedSite._id}`,
        { credentials: "include" }
      );
      res = await res.json();
      if (res.success) {
        res.data = res.data.filter((page) => page.slug === pageSlug);
        setPage(res.data[0]);
        setLoading(false);
      } else {
        console.error(res.error);
      }
    }
    if (selectedSite) {
      fetchPage();
    }
  }, [selectedSite, pageSlug]);

  if (loading || !selectedSite) {
    return <Loading />;
  }

  if (!loading && !page) {
    return <NotFound />;
  }

  // If the resource/site isn't published and the user isn't logged in, don't show the page
  if (!isAuth && (!page.published || !selectedSite.published)) {
    return <NotAllowed />;
  }

  return (
    <Fade in={typeof page != "undefined"}>
      <div className={classes.root}>
        <AdminBar />
        <Header />
        <AppBar />
        <Banner />
        <Breadcrumbs
          links={[
            {
              name: "Pages",
              url: `/${selectedSite.slug}/pages`,
            },
            {
              name: page.title,
              url: `/${selectedSite.slug}/pages/${page.slug}`,
            },
          ]}
        />
        <main className={classes.main}>
          <Paper className={classes.paper}>
            <Typography
              style={{ marginTop: "1rem" }}
              gutterBottom
              component="h2"
              variant="h5"
            >
              {page.title}
            </Typography>
            <div dangerouslySetInnerHTML={{ __html: page.content }}></div>
          </Paper>
        </main>
      </div>
    </Fade>
  );
}

export default Page;
